import { useState, type FormEvent } from "react";

const FORM_ID = "daimmbboh653ivfvogl0";
const SUBMIT_ADDR = "https://readdy.ai/api/form/daimmbboh653ivfvogl0";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
const labelClass = "mb-1.5 block text-sm font-medium text-foreground-800";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code ?? "";
      const isSpam = /spam|form data is spam/i.test(serverMsg);

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交未成功，请稍后重试或直接发送邮件联系我们。");
      }
    } catch (error) {
      setStatus("error");
      setFormError(error instanceof Error ? error.message : "网络异常，请稍后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-secondary-300 bg-secondary-50 px-6 py-14 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-500 text-background-50">
          <i className="ri-check-line text-2xl"></i>
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">已收到你的信息</h3>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
          感谢提供线索或建议。我们会先做一轮来源核实，通常在一到两个工作日内通过你留下的联系方式回复。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 whitespace-nowrap rounded-md border border-foreground-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      id={FORM_ID}
      data-readdy-form={FORM_ID}
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="contact-name">
            称呼 <span className="text-primary-500">*</span>
          </label>
          <input id="contact-name" name="name" type="text" required placeholder="怎么称呼你" className={inputClass} />
        </div>
        <div>
          <label className={labelClass} htmlFor="contact-email">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input id="contact-email" name="email" type="email" required placeholder="用于接收回复" className={inputClass} />
        </div>
        <div>
          <label className={labelClass} htmlFor="contact-topic">
            咨询类型
          </label>
          <select id="contact-topic" name="topic" defaultValue="线索投稿" className={inputClass}>
            <option value="线索投稿">线索投稿</option>
            <option value="信息更正">信息更正</option>
            <option value="内容建议">内容建议</option>
            <option value="转载授权">转载授权</option>
            <option value="其他">其他</option>
          </select>
        </div>
        <div>
          <label className={labelClass} htmlFor="contact-phone">
            联系方式（可选）
          </label>
          <input id="contact-phone" name="contact" type="text" placeholder="微信或其他联系方式" className={inputClass} />
        </div>
      </div>

      <div className="mt-5">
        <label className={labelClass} htmlFor="contact-detail">
          事项简述 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-detail"
          name="detail"
          required
          maxLength={500}
          rows={5}
          placeholder="简要说明事件经过，并尽量附上公开可查的来源链接（不超过 500 字）"
          className={`${inputClass} resize-none`}
        ></textarea>
        <p className="mt-1.5 text-xs text-foreground-500">请勿提交涉及个人隐私或无法核实来源的内容。</p>
      </div>

      <div className="anti-spam-extra-field">
        <label htmlFor="contact-website-alt">Website</label>
        <input
          id="contact-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="w-full"
        />
      </div>

      {status === "error" && formError && (
        <div className="mt-5 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5 text-base"></i>
          <span>{formError}</span>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-foreground-500">提交即表示你同意我们为核实内容而使用你提供的信息。</p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base"></i>
              提交中
            </>
          ) : (
            <>
              <i className="ri-send-plane-line text-base"></i>
              提交信息
            </>
          )}
        </button>
      </div>
    </form>
  );
}